import type { TODOLIST_TASK_STATUS, ITodolistFlow, ITodolistTaskGroup } from '~/types/todolist'
import type { ITodolistTemplate } from '~/types/todolist-template'

import config from '~/config'
import { request } from '~/utils/request'

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/app-key/-appKey-/device-type/-deviceType-/todo-list
 * @param appKey string The app key
 * @param deviceType string The device type
 * @returns A list of todolists with tasks
 */
export const getTodolists = ({
  appKey,
  deviceType,
}: {
  appKey: string
  deviceType: string
}): Promise<ITodolistFlow[]> => {
  return request({
    url: `${config.TODOLISTS_API}/app-key/${appKey}/device-type/${deviceType}/todo-list`,
  })
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/todo-list/-todoListID-
 * @param todolistId A todolist ID
 * @returns ITodolistFlow A todolist with tasks
 */
export const getTodolist = (todolistId: string): Promise<ITodolistFlow> => {
  return request({
    url: `${config.TODOLISTS_API}/todo-list/${todolistId}`,
  })
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/todo-list
 * @param data
 * @returns ITodolistFlow A todolist with tasks
 */
export const createTodolist = (data: {
  app_key: string
  device_type: string
  groups: ITodolistTaskGroup[]
  template_id: string
  title: string
}): Promise<ITodolistFlow> => {
  return request({
    data,
    method: 'POST',
    url: `${config.TODOLISTS_API}/todo-list`,
  })
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#put-/todo-list/-todoListID-
 * @param todolistId string The todolist ID
 * @param data Title & Groups
 * @returns ITodolistFlow The updated todolist
 */
export const updateTodolist = ({
  todolistId,
  ...data
}: {
  groups: ITodolistTaskGroup[]
  title: string
  todolistId: string
}): Promise<ITodolistFlow> => {
  return request({
    data,
    method: 'PUT',
    url: `${config.TODOLISTS_API}/todo-list/${todolistId}`,
  })
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/todo-list/-todoListID-/task-id/-taskID-/status/-status-
 * @param todolistId string The todolist ID
 * @param taskId string The task ID
 * @param status TODOLIST_TASK_STATUS The task status
 */
export const updateTodolistTaskStatus = ({
  status,
  taskId,
  todolistId,
}: {
  status: TODOLIST_TASK_STATUS
  taskId: string
  todolistId: string
}): Promise<void> => {
  return request({
    method: 'POST',
    url: `${config.TODOLISTS_API}/todo-list/${todolistId}/task-id/${taskId}/status/${status}`,
  })
}

// TEMPLATES

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/template
 * @returns ITodolistTemplate[] A list of todolist templates
 */
export const getTodolistTemplates = (): Promise<ITodolistTemplate[]> => {
  return request({
    url: `${config.TODOLISTS_API}/template`,
  })
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/template/{templateId}
 * @param templateId string The template ID
 * @returns ITodolistTemplate A todolist template
 */
export const getTodolistTemplate = (templateId: string): Promise<ITodolistTemplate> => {
  return request({
    url: `${config.TODOLISTS_API}/template/${templateId}`,
  })
}

export interface ICreateTodolistTemplatePayload {
  tasks: {
    description: string
    group_names: string[]
    previous_steps: string[]
    title: string
  }[]
  template: {
    description: string
    title: string
  }
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/template
 * @param data ICreateTodolistTemplatePayload The todolist template data
 * @returns ITodolistTemplate the created todolist template
 */
export const createTodolistTemplate = (data: ICreateTodolistTemplatePayload): Promise<ITodolistTemplate> => {
  return request({
    data,
    method: 'POST',
    url: `${config.TODOLISTS_API}/template`,
  })
}

/**
 * https://todolist-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/template/-templateID-
 * @param data ICreateTodolistTemplatePayload The todolist template data
 * @param templateId string The template ID
 * @returns ITodolistTemplate The updated todolist template
 */
export const updateTodolistTemplate = ({
  data,
  templateId,
}: {
  data: ICreateTodolistTemplatePayload
  templateId: string
}): Promise<ITodolistTemplate> => {
  return request({
    data,
    method: 'POST',
    url: `${config.TODOLISTS_API}/template/${templateId}`,
  })
}
